import StandaloneReplyEditorVM from 'components/editors/thread/text/reply/standaloneReply';
import { TextThreadEditorVMConfig } from 'components/editors/thread/text/textThread';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { ObservableArray, PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import TaskManager from 'managers/task';
import { TopicThread } from 'models/topicThread';
import './footer.html';
import './footer.scss';
import CommentRequestButtonVM from './requestButton';

components.preload('reply-editor-standalone');

const { discussionId } = settings;

export interface CommentFooterVMConfig {
    comment: SubscribableOrNullableValue<TopicThread>
    buttons: ObservableArray<CommentRequestButtonVM>

    pinMode: SubscribableOrNullableValue<boolean>
    onlyContent: SubscribableOrNullableValue<boolean>
    hideControls: SubscribableOrNullableValue<boolean>
    busy: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class CommentFooterVM {
    labels = labels;

    comment: Subscribable<TopicThread | undefined>
    id: Subscribable<string | undefined>
    topicId: Subscribable<string | undefined>

    buttons: ObservableArray<CommentRequestButtonVM>

    replyEditorConfig: TextThreadEditorVMConfig
    replyEditor = ko.observable<StandaloneReplyEditorVM>();

    onlyContent: Subscribable<boolean>
    hideControls: Subscribable<boolean>
    hasReplies: Subscribable<boolean>

    showFooterControls: Subscribable<boolean>

    canAddNewReply: Subscribable<boolean>

    isReady: Subscribable<boolean>
    busy: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: CommentFooterVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.comment = ko.flattenComputed(config.comment);
        this.id = this.comment.pluck(c => c.id);
        this.topicId = this.comment.pluck(c => c.topicId);
        const repliesCount = this.comment.pluck(c => c.repliesCount, 0);
        this.hasReplies = repliesCount.is(c => c > 0);
        this.hideControls = ko.flattenComputed(config.hideControls, false);

        const pinMode = ko.flattenComputed(config.pinMode, false);

        this.onlyContent = ko.flattenComputed(config.onlyContent, false);
        this.busy = ko.flattenComputed(config.busy, false);

        this.buttons = config.buttons;

        const currentUserManager = storage.get(CurrentUserManager, { discussionId });
        const user = currentUserManager.pluck('person');
        const isModeratorUser = user.pluck(u => u.isModerator, false);

        const taskManager = storage.get(TaskManager, { taskId: discussionId });
        const task = taskManager.pluck('task');

        this.showFooterControls = ko.pureComputed(() => pinMode() && isModeratorUser());       

        this.canAddNewReply = ko.strictComputed((task, user) => {
            if (task != undefined && user != undefined) {
                if (task.isHomework()) {
                    return false;
                }
                
                return !user.isObserver() ||
                    task.observerAccess() === enums.ObserverAccessTypes.CanComment.value ||
                    task.observerAccess() === enums.ObserverAccessTypes.CanCommentAndModeratorNotified.value;
            }

            return false;
        }, [task, user]);

        this.isReady = ko.pureComputed(() => this.showFooterControls() || !this.onlyContent());

        this.replyEditorConfig = { parentThread: this.comment }

        ko.applyBindingsToNode(info.element, {
            'visible': this.isReady,

            'css': {
                'topic__comment__footer': true
            }
        }, this);
    }
}